import React from 'react';
import PillarLayout from './PillarLayout';
import { SOCIAL_IMAGE } from '../../seo';

const P = ({ children }) => <p className="mb-4">{children}</p>;
const UL = ({ children }) => <ul className="mb-4 pl-5 list-disc space-y-1">{children}</ul>;
const Q = ({ children }) => (
  <blockquote className="my-5 pl-4" style={{ borderLeft: '3px solid var(--nb-accent)', fontFamily: 'var(--script)', color: 'var(--ink)', fontSize: 'var(--t-md)', lineHeight: 1.3 }}>
    {children}
  </blockquote>
);

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: 'What is an Engineering Operating System?',
  description: "An Engineering Operating System is the system a hardware organization runs its design loop inside — requirements, alternatives, CAD, analysis, test, correlation — so that engineering capability compounds project over project instead of resetting every time.",
  author: { '@type': 'Organization', name: 'Factory OS' },
  publisher: { '@type': 'Organization', name: 'Factory OS', url: 'https://factory-os.com', logo: { '@type': 'ImageObject', url: 'https://factory-os.com/og-image.png' } },
  datePublished: '2026-07-01',
  dateModified: '2026-07-24',
  image: SOCIAL_IMAGE,
  mainEntityOfPage: 'https://factory-os.com/resources/what-is-an-engineering-operating-system',
  about: ['Engineering operating system', 'Hardware engineering platform', 'PLM alternative', 'Engineering knowledge management', 'Engineering intelligence'],
  mentions: [
    { '@type': 'DefinedTerm', name: 'Engineering Operating System', '@id': 'https://factory-os.com/#engineering-operating-system' },
    { '@type': 'Thing', name: 'Product Lifecycle Management (PLM)' },
    { '@type': 'SoftwareApplication', name: 'Factory OS', '@id': 'https://factory-os.com/#software' }
  ],
};

const WhatIsEngineeringExecution = () => (
  <PillarLayout
    meta={{
      title: 'What is an Engineering Operating System? | Factory OS',
      description: "An Engineering Operating System is the system a hardware organization runs its design loop inside, so capability compounds instead of resetting every project. Not a PLM. Not a documentation tool. Here's the definition, what's in it, and what it isn't.",
      path: '/resources/what-is-an-engineering-operating-system',
    }}
    hero={{
      frame: 'Factory OS is the guided engineering loop for hardware. This essay covers the idea underneath it.',
      eyebrow: 'the engineering operating system · the category',
      h1: 'What is an Engineering Operating System?',
      thesis: "The system your organization runs on — so capability compounds instead of resetting every project.",
    }}
    articleSchema={articleSchema}
    related={[
      { href: '/resources/design-evidence-as-byproduct', title: 'Design evidence should be a byproduct, not a deliverable' },
      { href: '/resources/engineering-operating-system-vs-plm', title: 'Engineering Operating System vs. PLM' },
      { href: '/resources/iadt-verification-for-hardware-teams', title: 'IADT verification, applied to hardware teams' },
    ]}
    sections={[
      {
        heading: 'The one-sentence definition',
        body: (
          <>
            <P>
              An <b>Engineering Operating System</b> is the system a hardware organization runs its design loop inside — requirements, R&amp;D, CAD, data, analysis, test, correlation — where each phase has hard gates that require evidence to advance, every decision is remembered, and every project leaves the organization more capable than it found it.
            </P>
            <Q>
              The work happens in the system. The organization gets smarter because the work happened.
            </Q>
            <P>
              That's the whole shape. Everything else on this page either expands a piece of it or compares it to the categories it's not.
            </P>
          </>
        ),
      },
      {
        heading: 'What it is not',
        body: (
          <>
            <P>
              The most useful way to define a new category is to be specific about what it isn't. An Engineering Operating System is not:
            </P>
            <UL>
              <li><b>PLM</b> (Product Lifecycle Management). PLMs manage <i>artifacts</i> across the product's full lifetime — CAD vault, BOM-of-record, ECOs, change orders, parts library, supplier records, service history. PLMs answer "what part is on which assembly in which revision?" That's a real and necessary job. It is not the same job as "what did the organization learn, and is it available to the next engineer?"</li>
              <li><b>Requirements management.</b> Tools like Jama, DOORS, and Polarion catalog shall-statements and verification status. They're best-of-breed at one phase of the loop — Phase 1 — and they integrate with the rest of the toolchain. An Engineering Operating System runs the whole loop, and carries what it learns forward.</li>
              <li><b>CAD or CAE.</b> SolidWorks, NX, Fusion 360, ANSYS, Abaqus — these are the tools where geometry and analysis happen. The operating system <i>links to</i> them. It doesn't replace them.</li>
              <li><b>Engineering documentation software.</b> Wiki-shaped tools (Confluence, Notion, Coda) sit next to the work and ask the engineer to write the work up. An operating system puts the engineer inside the work, and the documentation — and the organization's growing capability — are side effects.</li>
              <li><b>Project management.</b> Linear, Jira, Asana, Monday — these track tasks. Tasks are not decisions, and decisions are what compound.</li>
              <li><b>An AI feature bolted onto any of the above.</b> A chatbot layered on a PLM or a wiki still inherits the underlying tool's blind spot: it can only surface what was written down. An operating system that runs the work has the decisions already, natively — nothing needs to be captured for the assistant to use it.</li>
            </UL>
            <P>
              An Engineering Operating System sits in the gap between requirements management and PLM, replaces the documentation tools that hardware teams were always going to drop under pressure, and turns every project into an increase in organizational capability instead of a reset to zero.
            </P>
          </>
        ),
      },
      {
        heading: 'The phases of the loop',
        body: (
          <>
            <P>
              "Operating system" implies a loop the system actually runs, the way a computer's OS runs every process on the machine. For hardware, the loop is seven phases:
            </P>
            <UL>
              <li><b>Requirements (REQS).</b> Shall-form statements with IADT verification method tagged at write time. Target value, tolerance, named verification approach.</li>
              <li><b>R&amp;D / Concept.</b> Trade studies with every alternative logged — material, process, geometry — plus cost and lead-time deltas. The selected concept carries a written justification.</li>
              <li><b>CAD.</b> Manufacturable model linked to the node. DFM check against the chosen process. Quote estimation generated from geometry. Drawing package and GD&amp;T captured.</li>
              <li><b>Data (DATA).</b> Loads, boundary conditions, material properties, environmental inputs. Real-world measurements and test data from prior revisions are pulled forward automatically.</li>
              <li><b>Analysis (CAE).</b> FEA, CFD, hand calcs — linked to the data inputs. Predicted values for stress, deflection, thermal, frequency stored with the model and method used.</li>
              <li><b>Test (TEST).</b> Test execution linked to the originating requirement. Measured values captured per requirement. Pass/fail computed against targets and tolerances. Failures open new revisions with the failure already cited.</li>
              <li><b>Correlation (CORR).</b> Predicted-vs-actual compared. Correlation percentage computed. Model accuracy tracked across revisions. Systematic over- or under-prediction flagged.</li>
            </UL>
            <P>
              Each transition is a hard gate. Phase N+1 cannot start until Phase N has the evidence it owes. The system enforces it, the engineers don't have to remember to — and each pass through the loop leaves a permanent record the next pass builds on.
            </P>
          </>
        ),
      },
      {
        heading: 'Why "operating system" is the right term',
        body: (
          <>
            <P>
              An operating system is the layer everything else runs on top of — the layer that persists, that every application shares, that gets more valuable the longer it's been running and the more it's accumulated. That's a deliberate choice of word, not a marketing flourish.
            </P>
            <P>
              Hardware has not had that layer. It has CAD (build the model), PLM (catalog the model), requirements management (catalog the shall-statements), test management (catalog the results), and ELN-style notebooks (write up what you did). All of these are <i>applications</i> — useful, narrow, and blind to each other. None of them is the layer underneath, accumulating what the organization has learned across all of them.
            </P>
            <Q>
              An Engineering Operating System is the bet that the layer underneath should exist — and that it should get smarter every time someone uses it.
            </Q>
            <P>
              When that layer exists, three properties show up that no adjacent category gives you:
            </P>
            <UL>
              <li>The decision is on the node at the moment it's made — not reconstructed from Slack later.</li>
              <li>The evidence is attached to the requirement it proves — not assembled into a traceability matrix overnight before a review.</li>
              <li>The next project starts from what the last one proved — not from a blank page someone has to repopulate from memory.</li>
            </UL>
          </>
        ),
      },
      {
        heading: 'Who needs one',
        body: (
          <>
            <P>
              An Engineering Operating System exists for the same reason a computer needs an operating system rather than a pile of standalone applications: not every team needs the rigor at every moment, but every team that ships a real product eventually does, and the cost of bolting it on later — after the knowledge is already scattered — is higher than the cost of running on it from day one.
            </P>
            <UL>
              <li><b>Hardware startups</b> — for whom a PLM is unreachable and a shared drive is fragile. <a href="/for/hardware-startups" style={{ color: 'var(--nb-accent)' }}>Read more →</a></li>
              <li><b>Defense and aerospace prototyping groups</b> — for whom a defensible engineering record is the deliverable a program review actually consumes. <a href="/for/defense-aerospace" style={{ color: 'var(--nb-accent)' }}>Read more →</a></li>
              <li><b>University competition teams</b> — for whom annual turnover means the only way to compound knowledge is to have it on the node when next year's team logs in. <a href="/for/university-teams" style={{ color: 'var(--nb-accent)' }}>Read more →</a></li>
              <li><b>Tier-2 / Tier-3 suppliers</b> — for whom the customer audit is going to ask for IADT-traceable evidence regardless of how the work was done.</li>
            </UL>
          </>
        ),
      },
      {
        heading: 'The bet underneath the category',
        body: (
          <>
            <P>
              Every category sits on a bet about what was wrong with the categories before it. The Engineering Operating System sits on this one:
            </P>
            <Q>
              Hardware organizations don't fail at engineering. They fail at remembering. And they fail at remembering because the tools that record were always separate from the tools that did the work — so nothing compounded.
            </Q>
            <P>
              If that's the right diagnosis, the fix isn't a better documentation tool — engineers will drop documentation tools under pressure, and they should. The fix is a system that runs the engineering itself, so the record and the capability increase are byproducts of doing the work, not a separate discipline anyone has to maintain.
            </P>
            <P>
              That's the category. That's the bet. The rest — remembering, protecting, coordinating, developing engineers, learning — is what the system actually does. The rest is implementation.
            </P>
          </>
        ),
      },
    ]}
  />
);

export default WhatIsEngineeringExecution;
